/**
 * DryRunBanner — top-of-page sticky banner that warns the user when MELI
 * is operating in DRY-RUN mode (real actions disabled).
 *
 * Polls /api/system/health every 30s. Hidden when real actions are allowed.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DryRunBanner = () => {
  const [dryRun, setDryRun] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem('dryrun-banner-dismissed') === '1'; } catch { return false; }
  });
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;

  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    const check = async () => {
      try {
        const { data } = await axios.get(`${API}/system/health`);
        if (cancelled) return;
        const env = data?.meli?.env || {};
        const inDryRun = !!env.dry_run || env.real_actions_allowed === false;
        setDryRun(inDryRun);
      } catch { /* silent */ }
    };
    check();
    const id = setInterval(check, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, [token]);

  if (!token || !dryRun || dismissed) return null;

  const handleDismiss = () => {
    try { sessionStorage.setItem('dryrun-banner-dismissed', '1'); } catch { /* ignore */ }
    setDismissed(true);
  };

  return (
    <div
      data-testid="dryrun-banner"
      className="bg-amber-50 border-b border-amber-300 px-4 py-2 flex items-center gap-3 text-amber-900 text-sm"
      role="alert"
    >
      <AlertTriangle className="w-4 h-4 text-amber-700 flex-shrink-0" />
      <div className="flex-1 leading-tight">
        <strong>MODO SIMULADO ATIVO:</strong>{' '}
        publicações, sincronizações e atualizações de preço para o Mercado Livre são
        gravadas como sucesso, mas <strong>não são enviadas ao MELI</strong>. Isso é proposital
        até liberarmos as ações reais.{' '}
        <a href="/settings/system-health" className="underline font-semibold hover:text-amber-700">
          Veja Saúde do Sistema
        </a>{' '}
        para detalhes.
      </div>
      <button
        onClick={handleDismiss}
        className="text-amber-700 hover:text-amber-900 p-1"
        data-testid="dryrun-banner-dismiss"
        aria-label="Dispensar aviso"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export default DryRunBanner;
